"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var createContext_1 = __importDefault(require("./createContext"));
var createTrace_1 = __importDefault(require("./createTrace"));
var EventStore_1 = __importDefault(require("./EventStore"));
var EventsManager_1 = __importDefault(require("./EventsManager"));
var isLocalStorageSupport = !!globalThis.localStorage;
/**
 * This is the main public entry point to the Data Collection web service.
 *
 * <h5>Example for initializing</h5>
 * <p>You may have a file</p>
 *
 * ```javascript
 * import {centralEvents} from 'seismic-core-service';
 * import apiUtils from './apiUtils';
 *
 * const { CentralEventsClient } = centralEvents;
 *
 * const centralEventsClient = new CentralEventsClient(eventData => apiUtils.common['1'].saveEvents(eventData));
 *
 * // Build up the event Trace, set only once
 * centralEventsClient.setTrace({
 *     application: APPLICATIONS.Web,
 *     device: detectDevices(window.navigator.userAgent),
 *     operatingSystem: detectOS(window.navigator.userAgent)
 * });
 *
 * // Build up the event Context (optional), set only once
 * centralEventsClient.setContext({
 *     tenant: getTenant(),
 *     domain: window.location.host,
 *     referrer: window.location.href,
 *     userAgent: window.navigator.userAgent
 * });
 *
 * // Call it to enable batching,
 * // otherwise, it will send the data once you call client.logEvent
 * centralEventsClient.startBatching();
 *
 * export default centralEventsClient;
 * ```
 *
 * <h5>Example for usage some where</h5>
 *
 * ```javascript
 * // Build up the actual event (ContentViewEventData is the only choice for now)
 * const eventData = {
 *     name: 'Test1.docx',
 *     userId: 'testUser1',
 *     contentId,
 *     contentVersionId,
 *     contentProfileId
 * };
 *
 * centralEventsUtils.logEvent(eventData);
 * ```
 */
var CentralEventsClient = /** @class */ (function () {
    /**
     * @param {function} apiCallFunc - how to call service api. Required.
     * @param {number} maxBatchingSize - batching poll size. 20 as default.
     * @param {number} ideal - after `ideal`ms, it will send out all events if any. 10min as default.
     * @param {number} retryTimes - how much times you want it retry when errors occurs on sending out. 3 as default.
     * @param {number} retryIdeal - Ideal between two retry call. 30s as default.
     */
    function CentralEventsClient(apiCallFunc, maxBatchingSize, ideal, retryTimes, retryIdeal) {
        if (maxBatchingSize === void 0) { maxBatchingSize = 20; }
        if (ideal === void 0) { ideal = 10 * 60 * 1000; }
        if (retryTimes === void 0) { retryTimes = 3; }
        if (retryIdeal === void 0) { retryIdeal = 30 * 1000; }
        if (typeof apiCallFunc !== 'function') {
            throw new Error('apiCallFunc is required');
        }
        this.enableBatching = false;
        this.maxBatchingSize = maxBatchingSize;
        this.eventsManager = new EventsManager_1.default(ideal, retryTimes, retryIdeal, apiCallFunc);
        this.initEventStore();
    }
    CentralEventsClient.prototype.setTrace = function (trace) {
        this.trace = createTrace_1.default(trace);
        this.initEventStore();
    };
    CentralEventsClient.prototype.setContext = function (context) {
        this.context = createContext_1.default(context);
        this.initEventStore();
    };
    CentralEventsClient.prototype.initEventStore = function () {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.eventStore = new EventStore_1.default(this.context, this.trace);
        this.eventsManager.setEventStore(this.eventStore);
    };
    CentralEventsClient.prototype.startBatching = function () {
        try {
            this.enableBatching = true;
            this.eventsManager.flushEvents();
            this.eventsManager.startTimer();
        }
        catch (e) {
            console.error(e);
            // Won't block user operation whatever the error is
        }
    };
    CentralEventsClient.prototype.logEvent = function (eventData) {
        try {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            var events = this.eventStore.addEvent(eventData);
            if (this.enableBatching && isLocalStorageSupport) {
                if (events.length >= this.maxBatchingSize) {
                    this.eventsManager.flushEvents();
                    this.eventsManager.resetTimer();
                }
            }
            else {
                this.eventsManager.flushEvents();
            }
        }
        catch (e) {
            console.error(e);
            // Won't block user operation whatever the error is
        }
    };
    CentralEventsClient.createTrace = createTrace_1.default;
    CentralEventsClient.createContext = createContext_1.default;
    return CentralEventsClient;
}());
exports.default = CentralEventsClient;
//# sourceMappingURL=centralEventsClient.js.map